import {
  enabledHeadModuleEntries,
  getModuleEntryByErpModule,
  getModulePermissionModules,
  headModuleEntriesFromCodes,
} from '@/config/moduleEntries';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import type { ErpModule } from '@/api/platform.api';
import { getAppWorkflowNav } from '@/utils/navigation';
import { hasUsedMultipleWorkspaceModules } from '@/utils/moduleScope';

/** Shared permission tags on every module — not used to infer app access. */
const SHARED_PERMISSION_MODULES = new Set([
  'EMPLOYEE',
  'ROLE',
  'ORGANIZATION',
  'DASHBOARD',
  'REPORT',
]);

/** Organization admin — may access every subscribed module after module login (permission-driven). */
export function isOrgOwner(permissions: string[], roleCode?: string | null): boolean {
  return permissions.includes('ORG_MANAGE') || roleCode === 'ORGANIZATION_OWNER';
}

/** Platform master operator (separate from org owner flows). */
export function isMasterOperator(user?: { masterAdmin?: boolean } | null): boolean {
  return user?.masterAdmin === true;
}

/** Role has permissions for a head-module app (mirrors backend EntryModuleAccessService). */
export function userHasDistinctModuleAccess(erpModule: ErpModule, permissions: string[]): boolean {
  const tags = getModulePermissionModules(erpModule).filter(
    (pm) => !SHARED_PERMISSION_MODULES.has(pm),
  );
  return permissions.some((code) => tags.some((tag) => code.startsWith(`${tag}_`) || code === tag));
}

/** Whether the user has at least one workspace nav item in this app (RBAC). */
export function canAccessModuleWorkspace(
  erpModule: ErpModule,
  has: (code: string) => boolean,
  permissions: string[],
  roleCode?: string | null,
): boolean {
  if (isOrgOwner(permissions, roleCode)) return true;
  const groups = getAppWorkflowNav(erpModule, has, permissions, () => true, roleCode);
  return groups.some((g) => g.items.length > 0);
}

/**
 * Apps shown in the launcher — driven by server {@code accessibleModules} (head apps only),
 * with login/localStorage fallback when the API has not loaded yet.
 */
export function getLauncherModules(
  enabledModules: string[],
  permissions: string[] = [],
  accessibleModules?: string[],
  roleCode?: string | null,
  hasPermission?: (code: string) => boolean,
) {
  if (accessibleModules?.length) {
    const fromServer = headModuleEntriesFromCodes(accessibleModules);
    if (fromServer.length > 0) {
      const headSubscribed = enabledHeadModuleEntries(enabledModules);
      return headSubscribed.length > 0
        ? fromServer.filter((entry) => headSubscribed.some((h) => h.erpModule === entry.erpModule))
        : fromServer;
    }
  }

  const subscribed = enabledHeadModuleEntries(enabledModules);

  if (isOrgOwner(permissions, roleCode)) {
    return subscribed;
  }

  const allowed = new Set<string>();
  const has =
    hasPermission ??
    ((code: string) =>
      permissions.includes('MASTER_ADMIN') || permissions.includes(code));

  for (const entry of subscribed) {
    if (userHasDistinctModuleAccess(entry.erpModule, permissions)) {
      allowed.add(entry.erpModule);
    }
    if (canAccessModuleWorkspace(entry.erpModule, has, permissions, roleCode)) {
      allowed.add(entry.erpModule);
    }
  }

  return subscribed.filter((entry) => allowed.has(entry.erpModule));
}

export function shouldShowAppLauncher(
  enabledModules: string[],
  permissions: string[] = [],
  accessibleModules?: string[],
  roleCode?: string | null,
  hasPermission?: (code: string) => boolean,
): boolean {
  const apps = getLauncherModules(
    enabledModules,
    permissions,
    accessibleModules,
    roleCode,
    hasPermission,
  );
  if (apps.length < 2) return false;
  if (isOrgOwner(permissions, roleCode)) return true;
  return hasUsedMultipleWorkspaceModules();
}

/** Modules visible in navigation (from login subscription, not org settings toggles). */
export function createAuthModuleChecker(authModules: string[]): (module: ErpModule) => boolean {
  if (authModules.length === 0) {
    return () => false;
  }
  return (module: ErpModule) => authModules.includes(module);
}

/** When org has exactly one subscribed app, land directly in that workspace. */
export function resolveSingleAppHomePath(
  enabledModules: string[],
  has: (code: string) => boolean,
  permissions: string[],
  accessibleModules?: string[],
  roleCode?: string | null,
): string | null {
  const apps = getLauncherModules(
    enabledModules,
    permissions,
    accessibleModules,
    roleCode,
    has,
  ).filter((entry) => canAccessModuleWorkspace(entry.erpModule, has, permissions, roleCode));

  if (apps.length !== 1) return null;
  return apps[0]?.homePath ?? null;
}

/** Default landing route for owners with access to multiple modules. */
export function resolveOwnerHomePath(enabledModules: string[]): string {
  for (const code of enabledModules) {
    const home = getModuleEntryByErpModule(code)?.homePath;
    if (home) return home;
  }
  return MODULE_HUB_PATH;
}
